/**
 * Botón "Descargar PDF" del visor oficial y de la vista previa del
 * borrador (OT-0027 Fase 5).
 *
 * Existe como componente y no como JSX repetido porque lo usan dos
 * rutas y debe verse y comportarse igual en ambas. La etiqueta cambia
 * según la versión esté aprobada o no — quien descarga un borrador tiene
 * que saber, antes de pulsar, que lo que baja no es el documento oficial.
 */

import { useTranslation } from 'react-i18next'
import { Download, Loader2 } from 'lucide-react'

import type { Artifact, ArtifactVersion } from '../../../api/artifacts'
import { useArtifactPdfDownload } from '../../../hooks/useArtifactPdfDownload'

export function DownloadPdfButton({
  artifact,
  version,
}: {
  artifact: Artifact
  version: ArtifactVersion
}) {
  const { t } = useTranslation('projects')
  const { download, isDownloading } = useArtifactPdfDownload()

  // Un documento aportado como archivo no sintetiza PDF: el backend
  // responde 409 y su descarga es la del original, que `UploadedFileViewer`
  // ya ofrece. La UI no ofrece acciones que el backend va a rechazar
  // (OT-0009).
  if (artifact.source_mode === 'upload') return null

  const isOfficial = version.approved_at !== null
  const label = isOfficial
    ? t('official.download_pdf')
    : t('official.download_pdf_draft')

  return (
    <button
      type="button"
      onClick={() =>
        void download({
          artifactId: artifact.id,
          versionId: version.id,
          fallbackFilename: `${artifact.code ?? artifact.name}.pdf`,
        })
      }
      disabled={isDownloading}
      title={label}
      className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink hover:border-line-strong hover:bg-surface-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isDownloading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <Download className="w-3.5 h-3.5" aria-hidden="true" />
      )}
      {isDownloading ? t('official.download_pdf_running') : label}
    </button>
  )
}
